import * as XLSX from 'xlsx';
import { formatMarginPercent } from '../../item-details/margin-approval.utils';
import { effectiveShippedQty } from '../../item-details/item-utils';

export interface OrderLinesDownloadOptions {
    orderNumber: string;
    /** When false, Disc % column is omitted (division 400). */
    includeDiscount: boolean;
    /** When false, Margin % and MCM % columns are omitted. */
    includeMarginColumns: boolean;
}

function toNumberOrBlank(value: unknown): number | string {
    if (value === null || value === undefined || value === '') {
        return '';
    }
    const n = Number(value);
    return Number.isFinite(n) ? n : String(value);
}

function buildHeaders(options: OrderLinesDownloadOptions): string[] {
    const headers = ['L#', 'Item No', 'Item Description', 'Ordered', 'Shipped', 'Rate'];
    if (options.includeDiscount) {
        headers.push('Disc %');
    }
    headers.push('Net Amount');
    if (options.includeMarginColumns) {
        headers.push('Margin %', 'MCM %');
    }
    headers.push('VAT (%)', 'Total');
    return headers;
}

function mapLineRow(line: any, options: OrderLinesDownloadOptions): (string | number)[] {
    const ordered = line?.quantity ?? line?.qty;
    const row: (string | number)[] = [
        toNumberOrBlank(line?.lineNumber ?? line?.lineNo),
        String(line?.itemNumber ?? line?.itemNo ?? ''),
        String(line?.itemName ?? line?.description ?? ''),
        toNumberOrBlank(ordered),
        toNumberOrBlank(effectiveShippedQty(line)),
        toNumberOrBlank(line?.rate)
    ];

    if (options.includeDiscount) {
        row.push(toNumberOrBlank(line?.discount));
    }

    row.push(toNumberOrBlank(line?.amountAfterDiscount ?? line?.netAmount));

    if (options.includeMarginColumns) {
        row.push(formatMarginPercent(line?.calculatedMargin));
        row.push(formatMarginPercent(line?.minimumMargin ?? line?.itemMcm));
    }

    row.push(toNumberOrBlank(line?.vat));
    row.push(toNumberOrBlank(line?.amountAfterVat ?? line?.lineAmount ?? line?.grossAmount));

    return row;
}

function buildFileName(orderNumber: string): string {
    const safeOrder = String(orderNumber || 'order').replace(/[^\w.-]+/g, '_');
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    return `OrderLines_${safeOrder}_${yyyy}${mm}${dd}.xlsx`;
}

/**
 * Downloads the current order lines as an .xlsx matching the Item Details V2 grid columns.
 */
export function downloadOrderLinesExcel(lines: any[], options: OrderLinesDownloadOptions): void {
    const headers = buildHeaders(options);
    const dataRows = (lines || []).map((line) => mapLineRow(line, options));
    const sheet = XLSX.utils.aoa_to_sheet([headers, ...dataRows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, 'Order Lines');
    XLSX.writeFile(workbook, buildFileName(options.orderNumber));
}
